import RecipeImage from '../components/RecipeImage';
import React, { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import { Link, useNavigate } from 'react-router-dom';
import { isFamilyFilterActive, getEditorIds, filterByFamily } from '../utils/familyFilter';
import {
    Box,
    Container,
    Grid,
    InputAdornment,
    Skeleton,
    TextField,
    Typography,
} from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import SearchIcon from '@mui/icons-material/Search';
import { fetchCollections } from '../utils/collectionsApi';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

const CAROUSEL_HEIGHT = 300;

const RecipeCarousel = ({ recipes }) => {
    const [index, setIndex] = useState(0);
    const [paused, setPaused] = useState(false);

    const prev = (e) => { e.preventDefault(); e.stopPropagation(); setIndex((i) => (i - 1 + recipes.length) % recipes.length); };
    const next = (e) => { e.preventDefault(); e.stopPropagation(); setIndex((i) => (i + 1) % recipes.length); };

    useEffect(() => {
        if (paused) return;
        const timer = setInterval(() => {
            setIndex((i) => (i + 1) % recipes.length);
        }, 5000);
        return () => clearInterval(timer);
    }, [paused, recipes.length]);

    return (
        <Box
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            sx={{
                position: 'relative',
                width: '100%',
                height: CAROUSEL_HEIGHT,
                mx: 'auto',
                borderRadius: 2,
                overflow: 'hidden',
                boxShadow: 3,
            }}
        >
            {/* Stack all slides — only the active one is opaque */}
            {recipes.map((r, i) => (
                <Box
                    key={r.id}
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        opacity: i === index ? 1 : 0,
                        transition: 'opacity 0.5s ease',
                        pointerEvents: i === index ? 'auto' : 'none',
                    }}
                >
                    <Link to={`/recipe/${r.id}`} state={{ id: r.id }} style={{ display: 'block', height: '100%' }}>
                        <RecipeImage
                            src={r.download_image_url || '/anchovie.jpg'}
                            alt={r.title}
                            sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                display: 'block',
                            }}
                        />
                        <Box
                            sx={{
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                px: 2,
                                pt: 4,
                                pb: 1.5,
                                background: 'linear-gradient(transparent, rgba(0,0,0,0.75))',
                            }}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.75, mb: 1 }}>
                                {recipes.map((_, di) => (
                                    <Box
                                        key={di}
                                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIndex(di); }}
                                        sx={{
                                            width: di === index ? 20 : 8,
                                            height: 8,
                                            borderRadius: 4,
                                            bgcolor: di === index ? '#fff' : 'rgba(255,255,255,0.5)',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                        }}
                                    />
                                ))}
                            </Box>
                            <Typography
                                variant="h6"
                                sx={{ color: '#fff', fontWeight: 700, lineHeight: 1.3 }}
                                className="clamp-recipe-text"
                            >
                                {r.title}
                            </Typography>
                            {(r.authors?.length > 0 || r.author) && (
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                    Recipe Courtesy of {r.authors?.length > 0
                                        ? r.authors.map(a => a.display_name).join(' & ')
                                        : r.author}
                                </Typography>
                            )}
                        </Box>
                    </Link>
                </Box>
            ))}

            {/* Prev button */}
            <Box
                onClick={prev}
                aria-label="previous recipe"
                role="button"
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: 48,
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    cursor: 'pointer',
                    filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.6))',
                }}
            >
                <ArrowBackIosNewIcon fontSize="small" />
            </Box>

            {/* Next button */}
            <Box
                onClick={next}
                aria-label="next recipe"
                role="button"
                sx={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    width: 48,
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    cursor: 'pointer',
                    filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.6))',
                }}
            >
                <ArrowForwardIosIcon fontSize="small" />
            </Box>

        </Box>
    );
};

const Home = () => {
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState('');
    const [carouselData, setCarouselData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [favorites, setFavorites] = useState([]);
    const [allRecipes, setAllRecipes] = useState({});
    const [recipesLoading, setRecipesLoading] = useState(true);

    useEffect(() => {
        const currentUserId = JSON.parse(localStorage.getItem('user'))?.id;
        fetchCollections()
            .then(cols => {
                const fav = cols.find(c => c.is_favorites && c.owner_id === currentUserId);
                setFavorites(fav?.recipe_ids || []);
            })
            .catch(() => {});

        const cached = JSON.parse(sessionStorage.getItem('recipeList'));
        if (cached) {
            const map = {};
            Object.values(cached.recipes || {}).flat().forEach(r => { map[r.id] = r; });
            setAllRecipes(map);
            setRecipesLoading(false);
        } else {
            fetch(`${process.env.REACT_APP_BACKEND_ADDRESS}/recipes`, {
                headers: { 'Authorization': 'Bearer ' + Cookies.get('token') },
            })
                .then(r => r.json())
                .then(data => {
                    sessionStorage.setItem('recipeList', JSON.stringify(data));
                    const map = {};
                    Object.values(data.recipes || {}).flat().forEach(r => { map[r.id] = r; });
                    setAllRecipes(map);
                })
                .catch(() => {})
                .finally(() => setRecipesLoading(false));
        }
    }, []);

    useEffect(() => {
        const api = `${process.env.REACT_APP_BACKEND_ADDRESS}/carousel`;
        const headers = { 'Authorization': 'Bearer ' + Cookies.get('token'), 'content-type': 'application/json' };

        async function fetchApi() {
            try {
                const data = await fetch(api, { method: 'GET', headers });
                const response = await data.json();
                const user = JSON.parse(localStorage.getItem('user')) || {};
                if (isFamilyFilterActive() && ['editor', 'admin'].includes(user.role)) {
                    const editorIds = await getEditorIds();
                    response.recipes = filterByFamily(response.recipes || [], editorIds);
                }
                setCarouselData(response);
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        }

        fetchApi();
    }, []);

    return (
        <Container sx={{ mt: 5, width: '90%', pb: 12 }}>
            {/* Search bar */}
            <Box
                component="form"
                onSubmit={(e) => { e.preventDefault(); if (searchQuery.trim()) navigate(`/search/${encodeURIComponent(searchQuery.trim())}`); }}
                sx={{ mb: 3 }}
            >
                <TextField
                    fullWidth
                    placeholder="Search recipes…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon color="action" />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ bgcolor: 'background.paper', borderRadius: 1 }}
                />
            </Box>

            {/* Hero: carousel + description side by side on desktop */}
            <Grid container spacing={4} alignItems="center" sx={{ mb: 4 }}>
                <Grid item xs={12} md={6}>
                    {loading ? (
                        <Skeleton
                            variant="rectangular"
                            animation="wave"
                            sx={{ width: '100%', height: CAROUSEL_HEIGHT, borderRadius: 2 }}
                        />
                    ) : (
                        carouselData?.recipes?.length > 0 && <RecipeCarousel recipes={carouselData.recipes} />
                    )}
                </Grid>
                <Grid item xs={12} md={6}>
                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, textAlign: { xs: 'center', md: 'left' } }}>
                        {loading ? <Skeleton animation="wave" width={280} /> : 'The Sposito Family Cookbook'}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ textAlign: { xs: 'center', md: 'left' }, lineHeight: 1.8 }}>
                        {loading ? (
                            <>
                                <Skeleton animation="wave" />
                                <Skeleton animation="wave" />
                                <Skeleton animation="wave" width="80%" />
                            </>
                        ) : (
                            'This cookbook was started by Frank and Florie Sposito in May 2015. It is being written for future generations but more specifically to be sure that our children, Vincenzo, Franco and Maria Sposito can have the recipes from their childhood captured and available for their families. It contains family recipes that have been gathered throughout the years and will grow with new recipes that our family enjoys.'
                        )}
                    </Typography>
                </Grid>
            </Grid>

            {/* Favorites — horizontal scroll row */}
            {(favorites.length > 0 || recipesLoading) && (
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <FavoriteIcon sx={{ color: 'error.main', fontSize: 20 }} />
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>Your Favorites</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1.5, overflowX: 'auto', pb: 1, scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
                        {recipesLoading ? (
                            [0, 1, 2, 3].map(i => (
                                <Box key={i} sx={{ width: 160, flexShrink: 0, borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
                                    <Skeleton variant="rectangular" animation="wave" sx={{ width: '100%', height: 100 }} />
                                    <Box sx={{ p: 1 }}>
                                        <Skeleton animation="wave" width="80%" />
                                    </Box>
                                </Box>
                            ))
                        ) : favorites.map(rid => {
                            const r = allRecipes[rid];
                            if (!r) return null;
                            return (
                                <Link key={rid} to={`/recipe/${rid}`} style={{ textDecoration: 'none', color: 'inherit', flexShrink: 0 }}>
                                    <Box sx={{
                                        width: 160, borderRadius: 2, overflow: 'hidden',
                                        border: '1px solid', borderColor: 'divider',
                                        '&:hover': { boxShadow: 3 },
                                    }}>
                                        <RecipeImage
                                            loading="lazy"
                                            src={r.download_image_url || '/anchovie.jpg'}
                                            alt={r.title}
                                            sx={{ width: '100%', height: 100, objectFit: 'cover', display: 'block' }}
                                        />
                                        <Box sx={{ p: 1 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{r.title}</Typography>
                                        </Box>
                                    </Box>
                                </Link>
                            );
                        })}
                    </Box>
                </Box>
            )}

        </Container>
    );
};

export default Home;
